import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, GripVertical } from 'lucide-react';
import { TextLabel } from '../types';

// ============================================================================
// TYPES
// ============================================================================

export interface TextOverlayProps {
  /** Array of text labels to display */
  labels: TextLabel[];
  /** Callback when labels change */
  onLabelsChange: (labels: TextLabel[]) => void;
  /** Container dimensions for coordinate conversion */
  containerWidth: number;
  containerHeight: number;
  /** Whether editing is disabled */
  disabled?: boolean;
  /** Current zoom level */
  zoom?: number;
}

interface DraggingState {
  labelId: string;
  startX: number;
  startY: number;
  labelStartX: number;
  labelStartY: number;
}

// ============================================================================
// SINGLE LABEL COMPONENT
// ============================================================================

interface LabelItemProps {
  label: TextLabel;
  containerWidth: number;
  containerHeight: number;
  zoom: number;
  disabled: boolean;
  onUpdate: (label: TextLabel) => void;
  onDelete: (id: string) => void;
  onDragStart: (e: React.MouseEvent, label: TextLabel) => void;
}

const LabelItem: React.FC<LabelItemProps> = ({
  label,
  containerWidth,
  containerHeight,
  zoom,
  disabled,
  onUpdate,
  onDelete,
  onDragStart,
}) => {
  const [isEditing, setIsEditing] = useState(label.isEditing || false);
  const [editText, setEditText] = useState(label.text);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    if (!disabled) {
      setIsEditing(true);
      setEditText(label.text);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (editText.trim() !== label.text) {
      onUpdate({ ...label, text: editText.trim(), isEditing: false });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === 'Escape') {
      setEditText(label.text);
      setIsEditing(false);
    }
  };

  // Calculate pixel position from normalized coordinates
  const pixelX = label.x * containerWidth;
  const pixelY = label.y * containerHeight;

  // Scale font size with zoom
  const scaledFontSize = label.fontSize;

  return (
    <div
      className="absolute group"
      style={{
        left: pixelX,
        top: pixelY,
        transform: 'translate(-50%, -50%)',
        cursor: disabled ? 'default' : 'move',
        zIndex: isEditing ? 100 : 10,
      }}
      onDoubleClick={handleDoubleClick}
    >
      {isEditing ? (
        <div className="relative">
          <textarea
            ref={inputRef}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="bg-white border-2 border-teal-500 rounded px-2 py-1 text-black outline-none resize-none min-w-[100px]"
            style={{
              fontSize: scaledFontSize,
              fontFamily: 'Arial, sans-serif',
              lineHeight: 1.2,
            }}
            rows={editText.split('\n').length}
          />
          <div className="absolute -top-6 left-0 text-xs text-teal-400 whitespace-nowrap">
            Enter to save, Shift+Enter for new line
          </div>
        </div>
      ) : (
        <div
          className="relative select-none"
          onMouseDown={(e) => !disabled && onDragStart(e, label)}
        >
          {/* Text content */}
          <div
            className="whitespace-pre-wrap text-center px-1"
            style={{
              fontSize: scaledFontSize,
              fontFamily: 'Arial, sans-serif',
              fontWeight: 'bold',
              color: label.color,
              textShadow: label.color === '#000000' 
                ? '0 0 3px white, 0 0 3px white, 0 0 3px white'
                : '0 0 3px black, 0 0 3px black',
              lineHeight: 1.2,
            }}
          >
            {label.text}
          </div>

          {/* Hover controls */}
          {!disabled && (
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(label.id);
                }}
                className="p-1 bg-red-500 hover:bg-red-600 text-white rounded shadow-lg"
                title="Delete label"
              >
                <X size={12} />
              </button>
              <div className="p-1 bg-slate-700 text-white rounded shadow-lg cursor-move">
                <GripVertical size={12} />
              </div>
            </div>
          )}

          {/* Selection indicator on hover */}
          {!disabled && (
            <div className="absolute inset-0 -m-1 border-2 border-transparent group-hover:border-teal-500 group-hover:border-dashed rounded pointer-events-none" />
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const TextOverlay: React.FC<TextOverlayProps> = ({
  labels,
  onLabelsChange,
  containerWidth,
  containerHeight,
  disabled = false,
  zoom = 1,
}) => {
  const [dragging, setDragging] = useState<DraggingState | null>(null);

  const handleDragStart = useCallback((e: React.MouseEvent, label: TextLabel) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDragging({
      labelId: label.id,
      startX: e.clientX,
      startY: e.clientY,
      labelStartX: label.x,
      labelStartY: label.y,
    });
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragging || containerWidth === 0 || containerHeight === 0) return;

    const deltaX = (e.clientX - dragging.startX) / zoom / containerWidth;
    const deltaY = (e.clientY - dragging.startY) / zoom / containerHeight;

    const newX = Math.max(0, Math.min(1, dragging.labelStartX + deltaX));
    const newY = Math.max(0, Math.min(1, dragging.labelStartY + deltaY));

    const updatedLabels = labels.map(label =>
      label.id === dragging.labelId
        ? { ...label, x: newX, y: newY }
        : label
    );
    onLabelsChange(updatedLabels);
  }, [dragging, labels, onLabelsChange, containerWidth, containerHeight, zoom]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  // Global mouse event listeners for dragging
  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  const handleUpdate = useCallback((updatedLabel: TextLabel) => {
    const updatedLabels = labels.map(label =>
      label.id === updatedLabel.id ? updatedLabel : label
    );
    onLabelsChange(updatedLabels);
  }, [labels, onLabelsChange]);

  const handleDelete = useCallback((id: string) => {
    onLabelsChange(labels.filter(label => label.id !== id));
  }, [labels, onLabelsChange]);

  if (containerWidth === 0 || containerHeight === 0) return null;

  return (
    <div 
      className="absolute inset-0 pointer-events-none"
      style={{ overflow: 'visible' }}
    >
      {labels.map(label => (
        <div key={label.id} className="pointer-events-auto">
          <LabelItem
            label={label}
            containerWidth={containerWidth}
            containerHeight={containerHeight}
            zoom={zoom}
            disabled={disabled}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onDragStart={handleDragStart}
          />
        </div>
      ))}
    </div>
  );
};

export default TextOverlay;
